import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Globe, Rss, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NewsItem {
  id: string;
  titulo: string;
  contenido: string;
  url: string;
  fuente: string;
  categoria_id: string | null;
  estado: 'publicado' | 'borrador';
  created_at: string;
  categoria?: {
    nombre: string;
  };
}

interface Category {
  id: string;
  nombre: string;
}

export default function News() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    titulo: '',
    contenido: '',
    url: '',
    fuente: '',
    categoria_id: '',
    estado: 'borrador' as const
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, []);

  const fetchNews = async () => {
    try {
      const { data: news, error } = await supabase
        .from('noticias')
        .select(`
          *,
          categoria:categorias(nombre)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(news || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data: categories, error } = await supabase
        .from('categorias')
        .select('*')
        .order('nombre');

      if (error) throw error;
      setCategories(categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddNews = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase.from('noticias').insert([{
        titulo: newItem.titulo,
        contenido: newItem.contenido,
        url: newItem.url,
        fuente: newItem.fuente,
        categoria_id: newItem.categoria_id || null,
        estado: newItem.estado
      }]);

      if (error) throw error;

      setSuccess('Noticia creada correctamente');
      setShowAddModal(false);
      setNewItem({
        titulo: '',
        contenido: '',
        url: '',
        fuente: '',
        categoria_id: '',
        estado: 'borrador'
      });
      fetchNews();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    if (!confirm('¿Estás seguro de eliminar esta noticia? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('noticias')
        .delete()
        .eq('id', newsId);

      if (error) throw error;
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  const handleUpdateStatus = async (newsId: string, newStatus: 'publicado' | 'borrador') => {
    try {
      const { error } = await supabase
        .from('noticias')
        .update({ estado: newStatus })
        .eq('id', newsId);

      if (error) throw error;
      fetchNews();
    } catch (error) {
      console.error('Error updating news status:', error);
    }
  };

  const filteredNews = news.filter(item => 
    item.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.contenido.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.fuente.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Noticias</h1>
        <div className="flex space-x-2">
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Añadir Noticia
          </button>
          <button className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
            <Rss className="w-5 h-5 mr-2" />
            Configurar Feeds
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar noticias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="space-y-4">
            {filteredNews.map((item) => (
              <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800">{item.titulo}</h3>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <Globe className="w-4 h-4 mr-1" />
                      <span>{item.fuente}</span>
                      {item.categoria && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{item.categoria.nombre}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-sm ${
                      item.estado === 'publicado' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.estado === 'publicado' ? 'Publicado' : 'Borrador'}
                    </span>
                    <button 
                      onClick={() => handleUpdateStatus(item.id, item.estado === 'publicado' ? 'borrador' : 'publicado')}
                      className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                      title={item.estado === 'publicado' ? 'Pasar a borrador' : 'Publicar'}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteNews(item.id)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                      title="Eliminar noticia"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {item.url && (
                      <a 
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-gray-500 hover:bg-gray-100 rounded-full"
                        title="Ver fuente original"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
                <p className="text-gray-600 text-sm line-clamp-2">{item.contenido}</p>
                <div className="mt-2 text-sm text-gray-500">
                  {new Date(item.created_at).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal para añadir noticia */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">Añadir Nueva Noticia</h2>
            <form onSubmit={handleAddNews}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  required
                  value={newItem.titulo}
                  onChange={(e) => setNewItem({...newItem, titulo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contenido
                </label>
                <textarea
                  required
                  rows={4}
                  value={newItem.contenido}
                  onChange={(e) => setNewItem({...newItem, contenido: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la fuente
                </label>
                <input
                  type="url"
                  value={newItem.url}
                  onChange={(e) => setNewItem({...newItem, url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuente
                </label>
                <input
                  type="text"
                  required
                  value={newItem.fuente}
                  onChange={(e) => setNewItem({...newItem, fuente: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría
                </label>
                <select
                  value={newItem.categoria_id}
                  onChange={(e) => setNewItem({...newItem, categoria_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Seleccionar categoría</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={newItem.estado}
                  onChange={(e) => setNewItem({...newItem, estado: e.target.value as 'borrador' | 'publicado'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="borrador">Borrador</option>
                  <option value="publicado">Publicado</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}